/**
 * Mocktest Generation API Service
 *
 * Service layer for 실전모의고사 generation API operations.
 * Handles auto/manual mocktest generation and preview.
 */

import api from './api';
import { normalizePassageDashes, preparePassageForGeneration } from '@/utils/languageGuard';
import type { GenerationQueueReason } from '@/types/api';

// ============================================================================
// Types
// ============================================================================

export interface PassageData {
  id: number;
  passage_id: string;
  passage: string;
  translation?: string;
}

export interface AutoConfig {
  typeQuantities: Record<string, number>;
  hardTypes: string[];
  easyTypes?: string[];
  allowDuplicates?: boolean;
}

export interface ManualRow {
  passageId: number;
  problemType: string;
  difficulty: 'Normal' | 'Hard' | 'Easy';
  editedPassage?: string;
  passageIdOverride?: string;
  rowOptions?: Record<string, unknown>;
}

export interface ManualConfig {
  rows: ManualRow[];
}

export interface MocktestGenerateRequest {
  credit_quote?: string;
  credit_retry_job_id?: string;
  workbook_id: number;
  mode: 'auto' | 'manual';
  config: AutoConfig | ManualConfig;
  passages: PassageData[];
  options?: Record<string, unknown>;
  transformation?: Record<string, unknown>;
}

export interface MocktestGenerateResponse {
  success: boolean;
  job_id: string;
  status: string;
  mocktest_number: number;
  total: number;
  skipped_duplicates?: number;
  queued?: boolean;
  queue_position?: number;
  queue_reason?: GenerationQueueReason | null;
  queue_message?: string | null;
}

export interface PreviewAssignment {
  passageId: number;
  passage_id: string;
  problemType: string;
  difficulty: string;
  passagePreview: string;
}

export interface PreviewResponse {
  success: boolean;
  assignments: PreviewAssignment[];
  total: number;
}

export interface MocktestSheet {
  id: number;
  name: string;
  count: number;
  created_at: string | null;
}

export interface MocktestSheetsResponse {
  success: boolean;
  sheets: MocktestSheet[];
  next_number: number;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Validate mocktest configuration without generating
 */
export async function validateConfig(
  mode: 'auto' | 'manual',
  config: AutoConfig | ManualConfig,
  passages: PassageData[]
): Promise<{ success: boolean; message: string }> {
  const response = await api.post<{ success: boolean; message: string }>(
    '/api/mocktest/validate',
    { mode, config, passages }
  );
  return response.data;
}

/**
 * Start mocktest generation job
 */
export async function generateMocktest(
  request: MocktestGenerateRequest
): Promise<MocktestGenerateResponse> {
  const passages = request.passages.map((passage) => ({
    ...passage,
    passage: request.mode === 'auto'
      ? preparePassageForGeneration(passage.passage, `지문 (${passage.passage_id})`)
      : normalizePassageDashes(passage.passage),
  }));
  const config = request.mode === 'manual'
    ? {
      ...request.config,
      rows: (request.config as ManualConfig).rows.map((row, index) => {
        const original = passages.find((passage) => passage.id === row.passageId);
        const passage = preparePassageForGeneration(
          row.editedPassage || original?.passage || '',
          `${index + 1}번 행 지문`,
        );
        return row.editedPassage ? { ...row, editedPassage: passage } : row;
      }),
    }
    : request.config;
  const response = await api.post<MocktestGenerateResponse>(
    '/api/mocktest/generate',
    { ...request, passages, config }
  );
  return response.data;
}

/**
 * Preview passage-type assignments for auto mode
 */
export async function previewAssignments(
  workbookId: number,
  config: AutoConfig,
  passages: PassageData[]
): Promise<PreviewResponse> {
  const response = await api.post<PreviewResponse>(
    '/api/mocktest/preview',
    {
      workbook_id: workbookId,
      config,
      passages,
    }
  );
  return response.data;
}

/**
 * Get list of existing mocktest sheets for a workbook
 */
export async function getMocktestSheets(
  workbookId: number
): Promise<MocktestSheetsResponse> {
  const response = await api.get<MocktestSheetsResponse>(
    `/api/mocktest/sheets/${workbookId}`
  );
  return response.data;
}

// ============================================================================
// Convenience exports
// ============================================================================

export const mocktestApi = {
  validateConfig,
  generateMocktest,
  previewAssignments,
  getMocktestSheets,
};

export default mocktestApi;
